/**
 * Map Editor Left Panel — layers, floor plans, photo category filters
 */

import { useState } from 'react';
import { useMapEditor } from '../MapEditorProvider';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../../shared/types/project';
import { api } from '../../../services/api';

export function LeftPanel({ hasOrtho, hasDsm, projectId, onPhotosImported }: { hasOrtho?: boolean; hasDsm?: boolean; projectId: string; onPhotosImported?: () => void }) {
  const { state, dispatch } = useMapEditor();
  const { photos, floorPlans, customCategories, categoryFilters } = state;
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  const allCategories = [...DEFAULT_PHOTO_CATEGORIES, ...customCategories];

  // Count photos per category
  const categoryCounts = new Map<string, number>();
  for (const p of photos) {
    categoryCounts.set(p.categoryId, (categoryCounts.get(p.categoryId) || 0) + 1);
  }

  return (
    <div style={styles.panel}>
      {/* Base Layers */}
      {(hasOrtho || hasDsm) && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>Base Layers</span>
          </div>
          {hasOrtho && (
            <div
              onClick={() => dispatch({ type: 'TOGGLE_ORTHO' })}
              style={{ ...styles.layerItem, ...(state.orthoVisible ? {} : { opacity: 0.4 }) }}
            >
              <button style={{ ...styles.visibilityBtn, color: state.orthoVisible ? '#f1f5f9' : '#475569' }}>
                {state.orthoVisible ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                )}
              </button>
              <div style={styles.layerInfo}>
                <div style={styles.layerName}>Orthomosaic</div>
              </div>
            </div>
          )}
          {hasDsm && (
            <div
              onClick={() => dispatch({ type: 'TOGGLE_DSM' })}
              style={{ ...styles.layerItem, ...(state.dsmVisible ? {} : { opacity: 0.4 }) }}
            >
              <button style={{ ...styles.visibilityBtn, color: state.dsmVisible ? '#f1f5f9' : '#475569' }}>
                {state.dsmVisible ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                )}
              </button>
              <div style={styles.layerInfo}>
                <div style={styles.layerName}>DSM / Elevation</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floor Plans */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTitle}>Floor Plans</span>
          <button
            onClick={() => dispatch({ type: 'SET_MODE', mode: 'place-overlay' })}
            style={styles.addBtn}
            title="Add floor plan"
          >+</button>
        </div>
        {floorPlans.length === 0 ? (
          <div style={styles.empty}>No floor plans</div>
        ) : (
          floorPlans
            .sort((a, b) => a.floorNumber - b.floorNumber)
            .map((fp) => (
              <div
                key={fp.id}
                onClick={() => dispatch({ type: 'SET_SELECTION', ids: [fp.id], itemType: 'overlay' })}
                style={{
                  ...styles.layerItem,
                  ...(state.selectedIds.includes(fp.id) ? styles.layerItemSelected : {}),
                }}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); dispatch({ type: 'UPDATE_FLOOR_PLAN', id: fp.id, updates: { visible: !fp.visible } }); }}
                  style={{ ...styles.visibilityBtn, color: fp.visible ? '#f1f5f9' : '#475569' }}
                  title={fp.visible ? 'Hide' : 'Show'}
                >
                  {fp.visible ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                  )}
                </button>
                <div style={styles.layerInfo}>
                  <div style={styles.layerName}>{fp.name}</div>
                  <div style={styles.layerMeta}>Floor {fp.floorNumber}</div>
                </div>
                <input
                  type="range"
                  min={0} max={1} step={0.05}
                  value={fp.opacity}
                  onChange={(e) => dispatch({ type: 'UPDATE_FLOOR_PLAN', id: fp.id, updates: { opacity: parseFloat(e.target.value) } })}
                  onClick={(e) => e.stopPropagation()}
                  style={styles.opacitySlider}
                  title={`Opacity: ${Math.round(fp.opacity * 100)}%`}
                />
              </div>
            ))
        )}
      </div>

      {/* Photo Categories */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTitle}>Photos</span>
          <button
            onClick={() => dispatch({ type: 'SET_MODE', mode: 'place-photo' })}
            style={styles.addBtn}
            title="Add photo"
          >+</button>
        </div>
        {/* Import from S3 */}
        <button
          onClick={async () => {
            setImporting(true);
            setImportResult(null);
            try {
              const result = await api.photo.registerPhotos(projectId);
              setImportResult(`Imported ${result.imported} (${result.gpsFound} with GPS)`);
              if (result.imported > 0) onPhotosImported?.();
            } catch (err: any) {
              setImportResult(err.message || 'Import failed');
            } finally {
              setImporting(false);
            }
          }}
          disabled={importing}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            width: 'calc(100% - 24px)', margin: '0 12px 8px',
            padding: '8px 10px', background: '#1e293b', border: '1px solid #334155',
            borderRadius: 6, color: '#94a3b8', fontSize: 11, fontWeight: 500,
            cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.6 : 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {importing ? 'Importing from S3...' : 'Import from S3'}
        </button>
        {importResult && (
          <div style={{ padding: '4px 12px 8px', fontSize: 10, color: '#64748b' }}>{importResult}</div>
        )}

        {allCategories.map((cat) => {
          const count = categoryCounts.get(cat.id) || 0;
          const isHidden = categoryFilters.has(cat.id);
          return (
            <div
              key={cat.id}
              onClick={() => dispatch({ type: 'TOGGLE_CATEGORY_FILTER', categoryId: cat.id })}
              style={{ ...styles.categoryItem, opacity: isHidden ? 0.4 : 1 }}
            >
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
              <span style={styles.categoryName}>{cat.name}</span>
              {count > 0 && <span style={styles.categoryCount}>{count}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    width: 220,
    background: 'rgba(0,0,0,0.75)',
    borderRight: '1px solid #333',
    overflowY: 'auto',
    flexShrink: 0,
  },
  section: {
    borderBottom: '1px solid #262626',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  addBtn: {
    width: 22,
    height: 22,
    borderRadius: 4,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  empty: {
    padding: '8px 12px',
    fontSize: 12,
    color: '#475569',
  },
  layerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'background 0.1s',
    borderLeft: '3px solid transparent',
  },
  layerItemSelected: {
    background: '#1e293b',
    borderLeft: '3px solid #4F7CFF',
  },
  visibilityBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  layerInfo: {
    flex: 1,
    minWidth: 0,
  },
  layerName: {
    fontSize: 12,
    fontWeight: 500,
    color: '#f1f5f9',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  layerMeta: {
    fontSize: 10,
    color: '#64748b',
  },
  opacitySlider: {
    width: 50,
    height: 4,
    accentColor: '#4F7CFF',
    cursor: 'pointer',
    flexShrink: 0,
  },
  categoryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  categoryName: {
    fontSize: 12,
    color: '#94a3b8',
    flex: 1,
  },
  categoryCount: {
    fontSize: 10,
    color: '#475569',
    background: '#1e293b',
    padding: '1px 6px',
    borderRadius: 10,
  },
};
